import { useState, useEffect } from "react";
import { 
  Database, 
  Cloud, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Pencil,
  Server,
  HardDrive
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/services/api";

const StatusBadge = ({ status }) => {
  const config = {
    connected: { variant: "default", icon: CheckCircle2, label: "Connected", className: "bg-green-100 text-green-700 border-green-200" },
    pending: { variant: "secondary", icon: AlertCircle, label: "Pending", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    error: { variant: "destructive", icon: AlertCircle, label: "Error", className: "bg-red-100 text-red-700 border-red-200" },
  };
  
  const { icon: Icon, label, className } = config[status] || config.pending;
  
  return (
    <Badge variant="outline" className={className}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const OracleConnectionCard = ({ connection, onTest, onDelete, onEdit, testing }) => (
  <Card className="connection-card" data-testid={`oracle-connection-${connection.id}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-orange-100 rounded-xl">
          <Database className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h3 className="font-semibold">{connection.name}</h3>
          <p className="text-sm text-muted-foreground">
            {connection.host}:{connection.port}/{connection.service_name}
          </p>
        </div>
      </div>
      <StatusBadge status={connection.status} />
    </div>
    <div className="flex gap-2 mt-4">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onTest(connection.id)}
        disabled={testing === connection.id}
        data-testid={`test-oracle-${connection.id}`}
      >
        {testing === connection.id ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <CheckCircle2 className="w-4 h-4 mr-1" />
        )}
        Test
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onEdit(connection)}
        data-testid={`edit-oracle-${connection.id}`}
      >
        <Pencil className="w-4 h-4 mr-1" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{connection.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(connection.id)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </Card>
);

const BigQueryConnectionCard = ({ connection, onTest, onDelete, onEdit, testing }) => (
  <Card className="connection-card" data-testid={`bigquery-connection-${connection.id}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <Cloud className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold">{connection.name}</h3>
          <p className="text-sm text-muted-foreground">
            Project: {connection.project_id}
          </p>
        </div>
      </div>
      <StatusBadge status={connection.status} />
    </div>
    <div className="flex gap-2 mt-4">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onTest(connection.id)}
        disabled={testing === connection.id}
        data-testid={`test-bigquery-${connection.id}`}
      >
        {testing === connection.id ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <CheckCircle2 className="w-4 h-4 mr-1" />
        )}
        Test
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onEdit(connection)}
        data-testid={`edit-bigquery-${connection.id}`}
      >
        <Pencil className="w-4 h-4 mr-1" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{connection.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(connection.id)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </Card>
);

const SQLServerConnectionCard = ({ connection, onTest, onDelete, onEdit, testing }) => (
  <Card className="connection-card" data-testid={`sqlserver-connection-${connection.id}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-indigo-100 rounded-xl">
          <HardDrive className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-semibold">{connection.name}</h3>
          <p className="text-sm text-muted-foreground">
            {connection.host}:{connection.port}/{connection.database}
          </p>
        </div>
      </div>
      <StatusBadge status={connection.status} />
    </div>
    <div className="flex gap-2 mt-4">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onTest(connection.id)}
        disabled={testing === connection.id}
        data-testid={`test-sqlserver-${connection.id}`}
      >
        {testing === connection.id ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <CheckCircle2 className="w-4 h-4 mr-1" />
        )}
        Test
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onEdit(connection)}
        data-testid={`edit-sqlserver-${connection.id}`}
      >
        <Pencil className="w-4 h-4 mr-1" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{connection.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(connection.id)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </Card>
);

const PostgreSQLConnectionCard = ({ connection, onTest, onDelete, onEdit, testing }) => (
  <Card className="connection-card" data-testid={`postgresql-connection-${connection.id}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-teal-100 rounded-xl">
          <Server className="w-5 h-5 text-teal-600" />
        </div>
        <div>
          <h3 className="font-semibold">{connection.name}</h3>
          <p className="text-sm text-muted-foreground">
            {connection.host}:{connection.port}/{connection.database}
          </p>
        </div>
      </div>
      <StatusBadge status={connection.status} />
    </div>
    <div className="flex gap-2 mt-4">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onTest(connection.id)}
        disabled={testing === connection.id}
        data-testid={`test-postgresql-${connection.id}`}
      >
        {testing === connection.id ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <CheckCircle2 className="w-4 h-4 mr-1" />
        )}
        Test
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onEdit(connection)}
        data-testid={`edit-postgresql-${connection.id}`}
      >
        <Pencil className="w-4 h-4 mr-1" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{connection.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(connection.id)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </Card>
);

export default function Connections() {
  const [oracleConnections, setOracleConnections] = useState([]);
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [sqlserverConnections, setSqlserverConnections] = useState([]);
  const [postgresConnections, setPostgresConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Oracle form state
  const [oracleDialogOpen, setOracleDialogOpen] = useState(false);
  const [oracleEditId, setOracleEditId] = useState(null);
  const [oracleForm, setOracleForm] = useState({
    name: "",
    host: "",
    port: "1521",
    service_name: "",
    username: "",
    password: ""
  });
  
  // BigQuery form state
  const [bqDialogOpen, setBqDialogOpen] = useState(false);
  const [bqEditId, setBqEditId] = useState(null);
  const [bqForm, setBqForm] = useState({
    name: "",
    project_id: "",
    credentials_json: ""
  });

  // SQL Server form state
  const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
  const [sqlEditId, setSqlEditId] = useState(null);
  const [sqlForm, setSqlForm] = useState({
    name: "",
    host: "",
    port: "1433",
    database: "",
    username: "",
    password: ""
  });

  // PostgreSQL form state
  const [pgDialogOpen, setPgDialogOpen] = useState(false);
  const [pgEditId, setPgEditId] = useState(null);
  const [pgForm, setPgForm] = useState({
    name: "",
    host: "",
    port: "5432",
    database: "",
    username: "",
    password: "",
    ssl_mode: "prefer"
  });

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const [oracle, bigquery, sqlserver, postgres] = await Promise.all([
        api.getOracleConnections(),
        api.getBigQueryConnections(),
        api.getSQLServerConnections(),
        api.getPostgreSQLConnections()
      ]);
      setOracleConnections(oracle);
      setBigqueryConnections(bigquery);
      setSqlserverConnections(sqlserver);
      setPostgresConnections(postgres);
    } catch (error) {
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOracle = async (e) => {
    e.preventDefault();
    try {
      if (oracleEditId) {
        await api.updateOracleConnection(oracleEditId, {
          ...oracleForm,
          port: parseInt(oracleForm.port)
        });
        toast.success("Oracle connection updated");
      } else {
        await api.createOracleConnection({
          ...oracleForm,
          port: parseInt(oracleForm.port)
        });
        toast.success("Oracle connection created");
      }
      setOracleDialogOpen(false);
      setOracleEditId(null);
      setOracleForm({ name: "", host: "", port: "1521", service_name: "", username: "", password: "" });
      fetchConnections();
    } catch (error) {
      toast.error(oracleEditId ? "Failed to update connection" : "Failed to create connection");
    }
  };

  const handleEditOracle = (connection) => {
    setOracleEditId(connection.id);
    setOracleForm({
      name: connection.name,
      host: connection.host,
      port: String(connection.port),
      service_name: connection.service_name,
      username: connection.username,
      password: connection.password || ""
    });
    setOracleDialogOpen(true);
  };

  const handleOracleDialogClose = (open) => {
    setOracleDialogOpen(open);
    if (!open) {
      setOracleEditId(null);
      setOracleForm({ name: "", host: "", port: "1521", service_name: "", username: "", password: "" });
    }
  };

  const handleCreateBigQuery = async (e) => {
    e.preventDefault();
    try {
      if (bqEditId) {
        await api.updateBigQueryConnection(bqEditId, bqForm);
        toast.success("BigQuery connection updated");
      } else {
        await api.createBigQueryConnection(bqForm);
        toast.success("BigQuery connection created");
      }
      setBqDialogOpen(false);
      setBqEditId(null);
      setBqForm({ name: "", project_id: "", credentials_json: "" });
      fetchConnections();
    } catch (error) {
      toast.error(bqEditId ? "Failed to update connection" : "Failed to create connection");
    }
  };

  const handleEditBigQuery = (connection) => {
    setBqEditId(connection.id);
    setBqForm({
      name: connection.name,
      project_id: connection.project_id,
      credentials_json: connection.credentials_json || ""
    });
    setBqDialogOpen(true);
  };

  const handleBqDialogClose = (open) => {
    setBqDialogOpen(open);
    if (!open) {
      setBqEditId(null);
      setBqForm({ name: "", project_id: "", credentials_json: "" });
    }
  };

  // SQL Server handlers
  const handleCreateSQLServer = async (e) => {
    e.preventDefault();
    try {
      if (sqlEditId) {
        await api.updateSQLServerConnection(sqlEditId, {
          ...sqlForm,
          port: parseInt(sqlForm.port)
        });
        toast.success("SQL Server connection updated");
      } else {
        await api.createSQLServerConnection({
          ...sqlForm,
          port: parseInt(sqlForm.port)
        });
        toast.success("SQL Server connection created");
      }
      setSqlDialogOpen(false);
      setSqlEditId(null);
      setSqlForm({ name: "", host: "", port: "1433", database: "", username: "", password: "" });
      fetchConnections();
    } catch (error) {
      toast.error(sqlEditId ? "Failed to update connection" : "Failed to create connection");
    }
  };

  const handleEditSQLServer = (connection) => {
    setSqlEditId(connection.id);
    setSqlForm({
      name: connection.name,
      host: connection.host,
      port: String(connection.port),
      database: connection.database,
      username: connection.username,
      password: connection.password || ""
    });
    setSqlDialogOpen(true);
  };

  const handleSqlDialogClose = (open) => {
    setSqlDialogOpen(open);
    if (!open) {
      setSqlEditId(null);
      setSqlForm({ name: "", host: "", port: "1433", database: "", username: "", password: "" });
    }
  };

  const testSQLServerConnection = async (id) => {
    setTesting(id);
    try {
      const result = await api.testSQLServerConnection(id);
      toast.success(result.message);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
      fetchConnections();
    } finally {
      setTesting(null);
    }
  };

  const deleteSQLServerConnection = async (id) => {
    try {
      await api.deleteSQLServerConnection(id);
      toast.success("Connection deleted");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  // PostgreSQL handlers
  const handleCreatePostgreSQL = async (e) => {
    e.preventDefault();
    try {
      if (pgEditId) {
        await api.updatePostgreSQLConnection(pgEditId, {
          ...pgForm,
          port: parseInt(pgForm.port)
        });
        toast.success("PostgreSQL connection updated");
      } else {
        await api.createPostgreSQLConnection({
          ...pgForm,
          port: parseInt(pgForm.port)
        });
        toast.success("PostgreSQL connection created");
      }
      setPgDialogOpen(false);
      setPgEditId(null);
      setPgForm({ name: "", host: "", port: "5432", database: "", username: "", password: "", ssl_mode: "prefer" });
      fetchConnections();
    } catch (error) {
      toast.error(pgEditId ? "Failed to update connection" : "Failed to create connection");
    }
  };

  const handleEditPostgreSQL = (connection) => {
    setPgEditId(connection.id);
    setPgForm({
      name: connection.name,
      host: connection.host,
      port: String(connection.port),
      database: connection.database,
      username: connection.username,
      password: connection.password || "",
      ssl_mode: connection.ssl_mode || "prefer"
    });
    setPgDialogOpen(true);
  };

  const handlePgDialogClose = (open) => {
    setPgDialogOpen(open);
    if (!open) {
      setPgEditId(null);
      setPgForm({ name: "", host: "", port: "5432", database: "", username: "", password: "", ssl_mode: "prefer" });
    }
  };

  const testPostgreSQLConnection = async (id) => {
    setTesting(id);
    try {
      const result = await api.testPostgreSQLConnection(id);
      toast.success(result.message);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
      fetchConnections();
    } finally {
      setTesting(null);
    }
  };

  const deletePostgreSQLConnection = async (id) => {
    try {
      await api.deletePostgreSQLConnection(id);
      toast.success("Connection deleted");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  const testOracleConnection = async (id) => {
    setTesting(id);
    try {
      const result = await api.testOracleConnection(id);
      toast.success(result.message);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
      fetchConnections();
    } finally {
      setTesting(null);
    }
  };

  const testBigQueryConnection = async (id) => {
    setTesting(id);
    try {
      const result = await api.testBigQueryConnection(id);
      toast.success(result.message);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
      fetchConnections();
    } finally {
      setTesting(null);
    }
  };

  const deleteOracleConnection = async (id) => {
    try {
      await api.deleteOracleConnection(id);
      toast.success("Connection deleted");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  const deleteBigQueryConnection = async (id) => {
    try {
      await api.deleteBigQueryConnection(id);
      toast.success("Connection deleted");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connections-page">
      <Tabs defaultValue="oracle" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="oracle" className="gap-2" data-testid="tab-oracle">
            <Database className="w-4 h-4" />
            Oracle ({oracleConnections.length})
          </TabsTrigger>
          <TabsTrigger value="bigquery" className="gap-2" data-testid="tab-bigquery">
            <Cloud className="w-4 h-4" />
            BigQuery ({bigqueryConnections.length})
          </TabsTrigger>
          <TabsTrigger value="sqlserver" className="gap-2" data-testid="tab-sqlserver">
            <HardDrive className="w-4 h-4" />
            SQL Server ({sqlserverConnections.length})
          </TabsTrigger>
          <TabsTrigger value="postgresql" className="gap-2" data-testid="tab-postgresql">
            <Server className="w-4 h-4" />
            PostgreSQL ({postgresConnections.length})
          </TabsTrigger>
        </TabsList>

        {/* Oracle Tab */}
        <TabsContent value="oracle" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Oracle Connections</h2>
              <p className="text-sm text-muted-foreground">Manage your Oracle database source connections</p>
            </div>
            <Dialog open={oracleDialogOpen} onOpenChange={handleOracleDialogClose}>
              <DialogTrigger asChild>
                <Button data-testid="add-oracle-connection">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Connection
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{oracleEditId ? "Edit Oracle Connection" : "Add Oracle Connection"}</DialogTitle>
                  <DialogDescription>
                    {oracleEditId ? "Update your Oracle database connection details" : "Enter your Oracle database connection details"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateOracle} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="oracle-name">Connection Name</Label>
                    <Input
                      id="oracle-name"
                      value={oracleForm.name}
                      onChange={(e) => setOracleForm({ ...oracleForm, name: e.target.value })}
                      placeholder="My Oracle DB"
                      required
                      data-testid="oracle-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="oracle-host">Host</Label>
                      <Input
                        id="oracle-host"
                        value={oracleForm.host}
                        onChange={(e) => setOracleForm({ ...oracleForm, host: e.target.value })}
                        placeholder="localhost"
                        required
                        data-testid="oracle-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oracle-port">Port</Label>
                      <Input
                        id="oracle-port"
                        type="number"
                        value={oracleForm.port}
                        onChange={(e) => setOracleForm({ ...oracleForm, port: e.target.value })}
                        placeholder="1521"
                        required
                        data-testid="oracle-port-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oracle-service">Service Name</Label>
                    <Input
                      id="oracle-service"
                      value={oracleForm.service_name}
                      onChange={(e) => setOracleForm({ ...oracleForm, service_name: e.target.value })}
                      placeholder="ORCL"
                      required
                      data-testid="oracle-service-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oracle-username">Username</Label>
                    <Input
                      id="oracle-username"
                      value={oracleForm.username}
                      onChange={(e) => setOracleForm({ ...oracleForm, username: e.target.value })}
                      placeholder="system"
                      required
                      data-testid="oracle-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oracle-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="oracle-password"
                        type={showPassword ? "text" : "password"}
                        value={oracleForm.password}
                        onChange={(e) => setOracleForm({ ...oracleForm, password: e.target.value })}
                        placeholder="••••••••"
                        required
                        data-testid="oracle-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-oracle-connection">
                      {oracleEditId ? "Update Connection" : "Save Connection"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {oracleConnections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="empty-state">
                <Database className="empty-state-icon" />
                <h3 className="font-semibold text-lg">No Oracle Connections</h3>
                <p className="text-muted-foreground text-sm mt-1 max-w-sm">
                  Add your first Oracle database connection to start building data pipelines
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {oracleConnections.map((conn) => (
                <OracleConnectionCard
                  key={conn.id}
                  connection={conn}
                  onTest={testOracleConnection}
                  onDelete={deleteOracleConnection}
                  onEdit={handleEditOracle}
                  testing={testing}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* BigQuery Tab */}
        <TabsContent value="bigquery" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">BigQuery Connections</h2>
              <p className="text-sm text-muted-foreground">Manage your Google BigQuery destinations</p>
            </div>
            <Dialog open={bqDialogOpen} onOpenChange={handleBqDialogClose}>
              <DialogTrigger asChild>
                <Button data-testid="add-bigquery-connection">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Connection
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{bqEditId ? "Edit BigQuery Connection" : "Add BigQuery Connection"}</DialogTitle>
                  <DialogDescription>
                    {bqEditId ? "Update your Google Cloud project details and service account credentials" : "Enter your Google Cloud project details and service account credentials"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateBigQuery} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bq-name">Connection Name</Label>
                    <Input
                      id="bq-name"
                      value={bqForm.name}
                      onChange={(e) => setBqForm({ ...bqForm, name: e.target.value })}
                      placeholder="My BigQuery Project"
                      required
                      data-testid="bq-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bq-project">Project ID</Label>
                    <Input
                      id="bq-project"
                      value={bqForm.project_id}
                      onChange={(e) => setBqForm({ ...bqForm, project_id: e.target.value })}
                      placeholder="my-gcp-project"
                      required
                      data-testid="bq-project-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bq-credentials">Service Account JSON</Label>
                    <Textarea
                      id="bq-credentials"
                      value={bqForm.credentials_json}
                      onChange={(e) => setBqForm({ ...bqForm, credentials_json: e.target.value })}
                      placeholder='{"type": "service_account", ...}'
                      className="font-mono text-sm h-40"
                      required
                      data-testid="bq-credentials-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Paste the entire contents of your service account JSON key file
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-bigquery-connection">
                      {bqEditId ? "Update Connection" : "Save Connection"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {bigqueryConnections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="empty-state">
                <Cloud className="empty-state-icon" />
                <h3 className="font-semibold text-lg">No BigQuery Connections</h3>
                <p className="text-muted-foreground text-sm mt-1 max-w-sm">
                  Add your BigQuery project credentials to use as a destination for your data pipelines
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bigqueryConnections.map((conn) => (
                <BigQueryConnectionCard
                  key={conn.id}
                  connection={conn}
                  onTest={testBigQueryConnection}
                  onDelete={deleteBigQueryConnection}
                  onEdit={handleEditBigQuery}
                  testing={testing}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* SQL Server Tab */}
        <TabsContent value="sqlserver" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">SQL Server Connections</h2>
              <p className="text-sm text-muted-foreground">Manage your Microsoft SQL Server data sources</p>
            </div>
            <Dialog open={sqlDialogOpen} onOpenChange={handleSqlDialogClose}>
              <DialogTrigger asChild>
                <Button data-testid="add-sqlserver-connection">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Connection
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{sqlEditId ? "Edit SQL Server Connection" : "Add SQL Server Connection"}</DialogTitle>
                  <DialogDescription>
                    {sqlEditId ? "Update your SQL Server database connection details" : "Enter your SQL Server database connection details"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSQLServer} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sql-name">Connection Name</Label>
                    <Input
                      id="sql-name"
                      value={sqlForm.name}
                      onChange={(e) => setSqlForm({ ...sqlForm, name: e.target.value })}
                      placeholder="My SQL Server DB"
                      required
                      data-testid="sqlserver-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sql-host">Server/Host</Label>
                      <Input
                        id="sql-host"
                        value={sqlForm.host}
                        onChange={(e) => setSqlForm({ ...sqlForm, host: e.target.value })}
                        placeholder="localhost"
                        required
                        data-testid="sqlserver-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sql-port">Port</Label>
                      <Input
                        id="sql-port"
                        type="number"
                        value={sqlForm.port}
                        onChange={(e) => setSqlForm({ ...sqlForm, port: e.target.value })}
                        placeholder="1433"
                        required
                        data-testid="sqlserver-port-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sql-database">Database Name</Label>
                    <Input
                      id="sql-database"
                      value={sqlForm.database}
                      onChange={(e) => setSqlForm({ ...sqlForm, database: e.target.value })}
                      placeholder="master"
                      required
                      data-testid="sqlserver-database-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sql-username">Username</Label>
                    <Input
                      id="sql-username"
                      value={sqlForm.username}
                      onChange={(e) => setSqlForm({ ...sqlForm, username: e.target.value })}
                      placeholder="sa"
                      required
                      data-testid="sqlserver-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sql-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="sql-password"
                        type={showPassword ? "text" : "password"}
                        value={sqlForm.password}
                        onChange={(e) => setSqlForm({ ...sqlForm, password: e.target.value })}
                        placeholder="••••••••"
                        required
                        data-testid="sqlserver-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-sqlserver-connection">
                      {sqlEditId ? "Update Connection" : "Save Connection"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {sqlserverConnections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="empty-state">
                <HardDrive className="empty-state-icon" />
                <h3 className="font-semibold text-lg">No SQL Server Connections</h3>
                <p className="text-muted-foreground text-sm mt-1 max-w-sm">
                  Add your SQL Server database to use as a data source for your pipelines
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sqlserverConnections.map((conn) => (
                <SQLServerConnectionCard
                  key={conn.id}
                  connection={conn}
                  onTest={testSQLServerConnection}
                  onDelete={deleteSQLServerConnection}
                  onEdit={handleEditSQLServer}
                  testing={testing}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* PostgreSQL Tab */}
        <TabsContent value="postgresql" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">PostgreSQL Connections</h2>
              <p className="text-sm text-muted-foreground">Manage your PostgreSQL data sources</p>
            </div>
            <Dialog open={pgDialogOpen} onOpenChange={handlePgDialogClose}>
              <Button onClick={() => setPgDialogOpen(true)} data-testid="add-postgresql-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add PostgreSQL
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{pgEditId ? "Edit" : "Add"} PostgreSQL Connection</DialogTitle>
                  <DialogDescription>
                    Connect to your PostgreSQL database server.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreatePostgreSQL} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Connection Name</Label>
                    <Input
                      value={pgForm.name}
                      onChange={(e) => setPgForm({ ...pgForm, name: e.target.value })}
                      placeholder="My PostgreSQL"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Host</Label>
                      <Input
                        value={pgForm.host}
                        onChange={(e) => setPgForm({ ...pgForm, host: e.target.value })}
                        placeholder="localhost"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        value={pgForm.port}
                        onChange={(e) => setPgForm({ ...pgForm, port: e.target.value })}
                        placeholder="5432"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Database</Label>
                    <Input
                      value={pgForm.database}
                      onChange={(e) => setPgForm({ ...pgForm, database: e.target.value })}
                      placeholder="postgres"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        value={pgForm.username}
                        onChange={(e) => setPgForm({ ...pgForm, username: e.target.value })}
                        placeholder="postgres"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={pgForm.password}
                          onChange={(e) => setPgForm({ ...pgForm, password: e.target.value })}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>SSL Mode</Label>
                    <select
                      value={pgForm.ssl_mode}
                      onChange={(e) => setPgForm({ ...pgForm, ssl_mode: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="disable">Disable</option>
                      <option value="allow">Allow</option>
                      <option value="prefer">Prefer (Default)</option>
                      <option value="require">Require</option>
                      <option value="verify-ca">Verify CA</option>
                      <option value="verify-full">Verify Full</option>
                    </select>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => handlePgDialogClose(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">{pgEditId ? "Update" : "Create"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {postgresConnections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Server className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold">No PostgreSQL Connections</h3>
                <p className="text-muted-foreground text-sm">
                  Add a PostgreSQL connection to get started with your data pipelines.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {postgresConnections.map((conn) => (
                <PostgreSQLConnectionCard
                  key={conn.id}
                  connection={conn}
                  onTest={testPostgreSQLConnection}
                  onDelete={deletePostgreSQLConnection}
                  onEdit={handleEditPostgreSQL}
                  testing={testing}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
